import * as THREE from 'three/webgpu';
import {
  Fn,
  attribute,
  uniform,
  uv,
  vec2,
  vec3,
  vec4,
  float,
  mix,
  smoothstep,
  length,
} from 'three/tsl';
import {
  DEFAULT_VISUAL_QUALITY_PRESET,
  type VisualQualityPreset,
} from './visualConfig';
import { hash21 } from './tslUtils';

// TSL uniform nodes are typed loosely — @types/three exports the Node
// generic as a default export that's awkward to re-import, and we only
// need the `.value` setter on the JS side plus passing the node into
// TSL Fn calls. Use ReturnType to keep the underlying type precise for
// `.value` assignment without importing internal node types.
type UniformNode = ReturnType<typeof uniform>;

// `Fn(([progress]) => ...)` defaults to the no-arg Fn overload in TS 5.x
// inference, so we need a helper that forces the args-tuple overload.
// We cast via unknown → readonly Node tuple to select the right Fn<TArgs>
// overload without pulling in the private `Node` type.
type FnArg = ReturnType<typeof float>;

interface Vec3 {
  x: number;
  y: number;
  z: number;
}

interface MuzzleFlashEffect {
  mesh: THREE.Mesh;
  material: THREE.MeshBasicNodeMaterial;
  uProgress: UniformNode;
  active: boolean;
  life: number;
  maxLife: number;
}

/**
 * Shared muzzle flash color Fn — radial billboard with hash noise, mixed
 * center/edge colors, and lifetime falloff driven by a progress float node.
 *
 * Returns vec4(rgb, alpha). Passed through both colorNode and emissiveNode
 * so the MRT emissive target picks it up for bloom.
 *
 * Declared once at module scope so the TSL compiler reuses the same
 * generated shader program for every muzzle flash pool entry; each pool
 * entry just passes its own uProgress uniform as the argument.
 */
// The `[progress]` destructure here matches the idiomatic TSL pattern
// used in three/src/nodes/math/Hash.js. The explicit tuple type annotation
// forces TypeScript to pick the args-tuple Fn overload rather than the
// no-arg overload (which is the default inference for `([x]) =>`).
const muzzleFlashColorFn = Fn(([progress]: [FnArg]) => {
  const p = float(progress);

  const centered = uv().sub(vec2(0.5));
  const dist = length(centered).mul(2.0);

  const radial = smoothstep(1.0, 0.0, dist);

  const hashInput = uv().mul(64.0).add(p.mul(7.0));
  const noise = hash21(hashInput).mul(0.35);

  const centerColor = vec3(1.0, 0.95, 0.8);
  const edgeColor = vec3(1.0, 0.6, 0.2);
  const baseColor = mix(edgeColor, centerColor, radial);

  const noisy = baseColor.add(noise.mul(radial).mul(0.3));
  const bright = noisy.mul(1.8);

  const lifetime = p.oneMinus();
  const alpha = radial.mul(lifetime).mul(lifetime);

  return vec4(bright, alpha);
});

interface TracerEffect {
  active: boolean;
  life: number;
  maxLife: number;
}

/**
 * Tracer line color Fn — warm yellow-orange core, intensity driven by
 * per-vertex aAlpha attribute (the update loop writes (1-t)^2 to aAlpha).
 * No per-instance uniforms: tracers share a single LineSegments mesh, so
 * the entire material is one compiled program.
 */
const tracerColorFn = Fn(() => {
  const vAlpha = float(attribute('aAlpha', 'float'));
  const color = vec3(1.0, 0.85, 0.5).mul(2.0);
  return vec4(color, vAlpha);
});

interface ImpactSparkEffect {
  obj: THREE.Points;
  material: THREE.PointsNodeMaterial;
  geometry: THREE.BufferGeometry;
  positions: Float32Array;
  velocities: Float32Array;
  uProgress: UniformNode;
  active: boolean;
  life: number;
  maxLife: number;
  particleCount: number;
}

/**
 * Impact spark color Fn — radial soft-circle falloff from uv()-0.5,
 * tinted warm, modulated by per-vertex aBrightness and lifetime.
 *
 * NOTE: WebGPU point primitives are hard-capped at 1 pixel. We still set
 * sizeNode (see `impactSparkSizeFn`) so that if this code is swapped to
 * an instanced sprite mesh, the sizing logic is already in place.
 */
const impactSparkColorFn = Fn(([progress]: [FnArg]) => {
  const p = float(progress);
  const vBrightness = float(attribute('aBrightness', 'float'));

  // pointUV is invalid WGSL; 1px WebGPU points need no circle mask
  const soft = float(1.0);

  const warm = vec3(1.0, 0.8, 0.4);
  const brightness = float(0.7).add(vBrightness.mul(0.6));
  const color = warm.mul(brightness).mul(1.6);

  const alpha = soft.mul(p.oneMinus());
  return vec4(color, alpha);
});

const impactSparkSizeFn = Fn(([progress]: [FnArg]) => {
  const p = float(progress);
  const vBrightness = float(attribute('aBrightness', 'float'));
  const shrink = p.mul(0.7).oneMinus();
  // Three applies size attenuation via sizeAttenuation on the material,
  // so we omit the perspective-correction factor.
  return float(IMPACT_SPARK_BASE_SIZE).mul(vBrightness).mul(shrink);
});

const MUZZLE_FLASH_POOL_BASE = 12;
const MUZZLE_FLASH_LIFE = 0.08;
const MUZZLE_FLASH_SIZE = 1.2;

const TRACER_POOL_BASE = 16;
const TRACER_LIFE = 0.12;

const IMPACT_SPARK_POOL_BASE = 10;
const IMPACT_SPARK_LIFE = 0.3;
const IMPACT_SPARK_PARTICLE_COUNT = 8;
const IMPACT_SPARK_GRAVITY = 18.0;
// Base pixel size factor applied by buildImpactSparkSize. Shared across all
// spark pool entries so a constant is sufficient.
const IMPACT_SPARK_BASE_SIZE = 12.0;

const _noopRaycast: THREE.Mesh['raycast'] = () => {};

export class AttackVisualizer {
  private readonly scene: THREE.Scene;

  private readonly muzzlePool: MuzzleFlashEffect[] = [];
  private muzzleCursor = 0;

  private readonly tracerPool: TracerEffect[] = [];
  private tracerCursor = 0;
  private readonly tracerGeometry: THREE.BufferGeometry;
  private readonly tracerMaterial: THREE.LineBasicNodeMaterial;
  private readonly tracerMesh: THREE.LineSegments;

  private readonly sparkPool: ImpactSparkEffect[] = [];
  private sparkCursor = 0;

  constructor(
    scene: THREE.Scene,
    qualityPreset: VisualQualityPreset = DEFAULT_VISUAL_QUALITY_PRESET,
  ) {
    this.scene = scene;
    const scale = qualityPreset.vfxParticleScale;

    const muzzleCount = Math.max(4, Math.round(MUZZLE_FLASH_POOL_BASE * scale));
    this.createMuzzlePool(muzzleCount);

    const tracerCount = Math.max(4, Math.round(TRACER_POOL_BASE * scale));
    const templatePositions = new Float32Array(tracerCount * 2 * 3);

    this.tracerGeometry = new THREE.BufferGeometry();
    this.tracerGeometry.setAttribute(
      'position',
      new THREE.BufferAttribute(templatePositions, 3),
    );

    // Attach aAlpha BEFORE building the material so the attribute() node
    // resolves against a valid BufferAttribute at compile time.
    const alphaPerVertex = new Float32Array(tracerCount * 2);
    this.tracerGeometry.setAttribute(
      'aAlpha',
      new THREE.BufferAttribute(alphaPerVertex, 1),
    );

    this.tracerMaterial = new THREE.LineBasicNodeMaterial({
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    // LineBasicNodeMaterial has no emissiveNode; additive blending + bloom
    // layer wiring routes the bright tracer color through the MRT output
    // channel for the selective bloom pipeline.
    const tracerColor = tracerColorFn();
    this.tracerMaterial.colorNode = tracerColor;
    this.tracerMaterial.opacityNode = tracerColor.a;

    this.tracerMesh = new THREE.LineSegments(this.tracerGeometry, this.tracerMaterial);
    this.tracerMesh.frustumCulled = false;
    this.tracerMesh.raycast = _noopRaycast;
    this.scene.add(this.tracerMesh);

    for (let i = 0; i < tracerCount; i++) {
      this.tracerPool.push({ active: false, life: 0, maxLife: TRACER_LIFE });
    }

    const sparkCount = Math.max(4, Math.round(IMPACT_SPARK_POOL_BASE * scale));
    this.createSparkPool(sparkCount);
  }

  showAttack(attackerPos: Vec3, targetPos: Vec3, _damage: number, missed: boolean = false): void {
    this.spawnMuzzleFlash(attackerPos);
    this.spawnTracer(attackerPos, targetPos);
    if (!missed) this.spawnImpactSparks(attackerPos, targetPos);
  }

  update(deltaMs: number): void {
    const dt = deltaMs / 1000;
    this.updateMuzzleFlashes(dt);
    this.updateTracers(dt);
    this.updateImpactSparks(dt);
  }

  dispose(): void {
    for (const effect of this.muzzlePool) {
      effect.mesh.geometry.dispose();
      effect.material.dispose();
      this.scene.remove(effect.mesh);
    }

    this.tracerGeometry.dispose();
    this.tracerMaterial.dispose();
    this.scene.remove(this.tracerMesh);

    for (const effect of this.sparkPool) {
      effect.geometry.dispose();
      effect.material.dispose();
      this.scene.remove(effect.obj);
    }
  }

  private createMuzzlePool(size: number): void {
    for (let i = 0; i < size; i++) {
      const geometry = new THREE.PlaneGeometry(MUZZLE_FLASH_SIZE, MUZZLE_FLASH_SIZE);

      // Each pool entry owns its own uProgress uniform so flashes animate
      // independently — NodeMaterial shares the compiled program but each
      // material instance carries its own uniform buffer.
      const uProgress = uniform(0);
      const material = new THREE.MeshBasicNodeMaterial({
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
        side: THREE.DoubleSide,
      });
      const flashColor = muzzleFlashColorFn(uProgress);
      material.colorNode = flashColor;
      // MeshBasicNodeMaterial has no emissiveNode; additive blending + bloom
      // layer wiring handles the MRT output for the selective bloom pipeline.
      material.opacityNode = flashColor.a;

      const mesh = new THREE.Mesh(geometry, material);
      mesh.visible = false;
      mesh.frustumCulled = false;
      mesh.raycast = _noopRaycast;
      // Auto-billboard: face the camera each frame.
      mesh.onBeforeRender = ((
        _renderer: unknown,
        _scene: THREE.Scene,
        camera: THREE.Camera,
      ) => {
        mesh.quaternion.copy(camera.quaternion);
      }) as THREE.Mesh['onBeforeRender'];
      this.scene.add(mesh);

      this.muzzlePool.push({
        mesh,
        material,
        uProgress,
        active: false,
        life: 0,
        maxLife: MUZZLE_FLASH_LIFE,
      });
    }
  }

  private spawnMuzzleFlash(pos: Vec3): void {
    const effect = this.muzzlePool[this.muzzleCursor % this.muzzlePool.length];
    this.muzzleCursor += 1;

    effect.active = true;
    effect.life = 0;
    effect.mesh.visible = true;
    effect.mesh.position.set(pos.x, pos.y, pos.z);
    effect.uProgress.value = 0;
  }

  private updateMuzzleFlashes(dt: number): void {
    for (const effect of this.muzzlePool) {
      if (!effect.active) continue;
      effect.life += dt;
      const t = effect.life / effect.maxLife;
      if (t >= 1) {
        effect.active = false;
        effect.mesh.visible = false;
        continue;
      }
      effect.uProgress.value = t;
    }
  }

  private spawnTracer(startPos: Vec3, endPos: Vec3): void {
    const idx = this.tracerCursor % this.tracerPool.length;
    this.tracerCursor += 1;

    const tracer = this.tracerPool[idx];
    tracer.active = true;
    tracer.life = 0;

    const posAttr = this.tracerGeometry.attributes.position as THREE.BufferAttribute;
    const v0 = idx * 2;
    const v1 = v0 + 1;
    posAttr.setXYZ(v0, startPos.x, startPos.y, startPos.z);
    posAttr.setXYZ(v1, endPos.x, endPos.y, endPos.z);
    posAttr.needsUpdate = true;

    const alphaAttr = this.tracerGeometry.attributes.aAlpha as THREE.BufferAttribute;
    alphaAttr.setX(v0, 1.0);
    alphaAttr.setX(v1, 1.0);
    alphaAttr.needsUpdate = true;
  }

  private updateTracers(dt: number): void {
    const alphaAttr = this.tracerGeometry.attributes.aAlpha as THREE.BufferAttribute;
    const posAttr = this.tracerGeometry.attributes.position as THREE.BufferAttribute;
    let needsUpdate = false;

    for (let i = 0; i < this.tracerPool.length; i++) {
      const tracer = this.tracerPool[i];
      if (!tracer.active) continue;

      tracer.life += dt;
      const t = tracer.life / tracer.maxLife;

      if (t >= 1) {
        tracer.active = false;
        // Collapse to the last endpoint (not origin) so the geometry's
        // bounding sphere isn't permanently anchored at (0,0,0).
        const v0 = i * 2;
        alphaAttr.setX(v0, 0);
        alphaAttr.setX(v0 + 1, 0);
        const endX = posAttr.getX(v0 + 1);
        const endY = posAttr.getY(v0 + 1);
        const endZ = posAttr.getZ(v0 + 1);
        posAttr.setXYZ(v0, endX, endY, endZ);
        needsUpdate = true;
        continue;
      }

      const alpha = (1.0 - t) * (1.0 - t);
      const v0 = i * 2;
      alphaAttr.setX(v0, alpha);
      alphaAttr.setX(v0 + 1, alpha);
      needsUpdate = true;
    }

    if (needsUpdate) {
      alphaAttr.needsUpdate = true;
      posAttr.needsUpdate = true;
    }
  }

  private createSparkPool(size: number): void {
    for (let i = 0; i < size; i++) {
      const particleCount = IMPACT_SPARK_PARTICLE_COUNT;
      const positions = new Float32Array(particleCount * 3);
      const velocities = new Float32Array(particleCount * 3);
      const brightness = new Float32Array(particleCount);

      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      geometry.setAttribute('aBrightness', new THREE.BufferAttribute(brightness, 1));

      // Per-instance uProgress keeps spark pools independently animated.
      const uProgress = uniform(0);
      const material = new THREE.PointsNodeMaterial({
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      });
      const sparkColor = impactSparkColorFn(uProgress);
      material.colorNode = sparkColor;
      // PointsNodeMaterial has no emissiveNode; additive blending + bloom
      // layer wiring handles the MRT output for the selective bloom pipeline.
      material.opacityNode = sparkColor.a;
      // sizeNode is advisory (see impactSparkSizeFn comment for why): it is
      // honored only by the sprite-render path of PointsNodeMaterial.
      material.sizeNode = impactSparkSizeFn(uProgress);

      const points = new THREE.Points(geometry, material);
      points.visible = false;
      points.frustumCulled = false;
      points.raycast = _noopRaycast;
      this.scene.add(points);

      this.sparkPool.push({
        obj: points,
        material,
        geometry,
        positions,
        velocities,
        uProgress,
        active: false,
        life: 0,
        maxLife: IMPACT_SPARK_LIFE,
        particleCount,
      });
    }
  }

  private spawnImpactSparks(attackerPos: Vec3, targetPos: Vec3): void {
    const effect = this.sparkPool[this.sparkCursor % this.sparkPool.length];
    this.sparkCursor += 1;

    effect.active = true;
    effect.life = 0;
    effect.obj.visible = true;
    effect.uProgress.value = 0;

    // Direction: from attacker toward target (sparks spray AWAY from attacker)
    const dx = targetPos.x - attackerPos.x;
    const dy = targetPos.y - attackerPos.y;
    const dz = targetPos.z - attackerPos.z;
    const len = Math.sqrt(dx * dx + dy * dy + dz * dz) || 1;
    const nx = dx / len;
    const ny = dy / len;
    const nz = dz / len;

    const brightnessAttr = effect.geometry.attributes.aBrightness as THREE.BufferAttribute;

    for (let i = 0; i < effect.particleCount; i++) {
      const i3 = i * 3;
      // Start at impact (target) position
      effect.positions[i3] = targetPos.x;
      effect.positions[i3 + 1] = targetPos.y;
      effect.positions[i3 + 2] = targetPos.z;

      // Cone spread away from attacker: base direction + random deviation
      const spreadAngle = 0.5; // radians of cone half-angle
      const randTheta = Math.random() * Math.PI * 2;
      const randSpread = Math.random() * spreadAngle;

      // Build a tangent frame around the normal
      let tx: number, ty: number, tz: number;
      if (Math.abs(ny) < 0.9) {
        tx = nz; ty = 0; tz = -nx;
      } else {
        tx = 0; ty = -nz; tz = ny;
      }
      const tLen = Math.sqrt(tx * tx + ty * ty + tz * tz) || 1;
      tx /= tLen; ty /= tLen; tz /= tLen;

      // Binormal
      const bx = ny * tz - nz * ty;
      const by = nz * tx - nx * tz;
      const bz = nx * ty - ny * tx;

      const cosSpread = Math.cos(randSpread);
      const sinSpread = Math.sin(randSpread);
      const cosTheta = Math.cos(randTheta);
      const sinTheta = Math.sin(randTheta);

      const vx = nx * cosSpread + (tx * cosTheta + bx * sinTheta) * sinSpread;
      const vy = ny * cosSpread + (ty * cosTheta + by * sinTheta) * sinSpread;
      const vz = nz * cosSpread + (tz * cosTheta + bz * sinTheta) * sinSpread;

      const speed = 6.0 + Math.random() * 8.0;
      effect.velocities[i3] = vx * speed;
      effect.velocities[i3 + 1] = vy * speed;
      effect.velocities[i3 + 2] = vz * speed;

      brightnessAttr.setX(i, 0.5 + Math.random() * 0.5);
    }

    effect.geometry.attributes.position.needsUpdate = true;
    brightnessAttr.needsUpdate = true;
  }

  private updateImpactSparks(dt: number): void {
    for (const effect of this.sparkPool) {
      if (!effect.active) continue;
      effect.life += dt;
      const t = effect.life / effect.maxLife;

      if (t >= 1) {
        effect.active = false;
        effect.obj.visible = false;
        continue;
      }

      effect.uProgress.value = t;

      const posAttr = effect.geometry.attributes.position as THREE.BufferAttribute;
      for (let i = 0; i < effect.particleCount; i++) {
        const i3 = i * 3;
        posAttr.setXYZ(
          i,
          posAttr.getX(i) + effect.velocities[i3] * dt,
          posAttr.getY(i) + effect.velocities[i3 + 1] * dt - 0.5 * IMPACT_SPARK_GRAVITY * dt * dt,
          posAttr.getZ(i) + effect.velocities[i3 + 2] * dt,
        );
        // Apply gravity to velocity for next frame
        effect.velocities[i3 + 1] -= IMPACT_SPARK_GRAVITY * dt;
      }
      posAttr.needsUpdate = true;
    }
  }
}
